import React, {
  useContext,
  createContext,
  useCallback,
  useState,
  useEffect
} from 'react'

import {
  useGiftCardBalanceLazyQuery,
  useGiftCardExtensionBalanceLazyQuery
} from 'src/apollo/onlineOrdering'

export type GiftCardContextType = {
  applyGiftCard: (
    cartGuid: string,
    giftCard: string,
    captchaToken?: string
  ) => Promise<void>
  applyGiftCardExtension: (
    cartGuid: string,
    giftCard: string,
    captchaToken: string,
    pinCode?: string
  ) => Promise<void>
  removeGiftCard: () => void
  loadingGiftCard: boolean
  giftCardNumber: string
  giftCardGuid?: string
  verificationCode?: string
  giftCardAvailableBalance: number
  giftCardError: string
}

export const GiftCardContext = createContext<GiftCardContextType | undefined>(
  undefined
)

export const GiftCardContextProvider = (props: React.PropsWithChildren<{}>) => {
  const [giftCardBalanceQuery, { data, error }] = useGiftCardBalanceLazyQuery({
    fetchPolicy: 'no-cache'
  })
  const [
    giftCardBalanceExtensionQuery,
    { data: extensionData, error: extensionError }
  ] = useGiftCardExtensionBalanceLazyQuery({ fetchPolicy: 'no-cache' })
  const [loadingGiftCard, setLoadingGiftCard] = useState(false)
  const [giftCardNumber, setGiftCardNumber] = useState('')
  const [giftCardGuid, setGiftCardGuid] = useState<string | undefined>()
  const [verificationCode, setVerificationCode] = useState<string>()
  const [giftCardAvailableBalance, setGiftCardAvailableBalance] = useState(0.0)
  const [giftCardError, setGiftCardError] = useState('')

  const removeGiftCard = useCallback(() => {
    setGiftCardNumber('')
    setGiftCardGuid(undefined)
    setVerificationCode(undefined)
    setGiftCardAvailableBalance(0)
  }, [setGiftCardNumber, setGiftCardGuid, setGiftCardAvailableBalance])

  useEffect(() => {
    // Even though the return data for giftCardBalanceQuery is a union of GiftCardBalanceInquiryResponse and GiftCardBalanceInquiryError,
    // GiftCardBalanceInquiryError is defined as a child class of ApolloError, so it comes back as a graphQLError.
    if (error?.graphQLErrors?.length && error.graphQLErrors[0]) {
      setGiftCardError(error.graphQLErrors[0].message)
      removeGiftCard()
    } else if (
      extensionError?.graphQLErrors?.length &&
      extensionError.graphQLErrors[0]
    ) {
      setGiftCardError(extensionError.graphQLErrors[0].message)
      removeGiftCard()
    } else {
      setGiftCardError('')
    }
    setLoadingGiftCard(false)
  }, [error, extensionError, setGiftCardError, removeGiftCard])

  useEffect(() => {
    if (
      data?.giftCardBalanceInquiry?.__typename ===
      'GiftCardBalanceInquiryResponse'
    ) {
      setGiftCardNumber(data.giftCardBalanceInquiry.cardNumber)
      setGiftCardGuid(data.giftCardBalanceInquiry.toastCardGuid ?? undefined)
      setGiftCardAvailableBalance(data.giftCardBalanceInquiry.availableBalance)
      setGiftCardError('')
    } else if (
      data?.giftCardBalanceInquiry?.__typename === 'GiftCardBalanceInquiryError'
    ) {
      setGiftCardError(data.giftCardBalanceInquiry.message)
      removeGiftCard()
    }
    setLoadingGiftCard(false)
  }, [
    data,
    setGiftCardNumber,
    setGiftCardAvailableBalance,
    setGiftCardError,
    removeGiftCard
  ])

  useEffect(() => {
    if (
      extensionData?.giftCardExtensionBalanceInquiry?.__typename ===
      'GiftCardBalanceInquiryResponse'
    ) {
      setGiftCardNumber(
        extensionData.giftCardExtensionBalanceInquiry.cardNumber
      )
      setGiftCardGuid(
        extensionData.giftCardExtensionBalanceInquiry.toastCardGuid ?? undefined
      )
      setGiftCardAvailableBalance(
        extensionData.giftCardExtensionBalanceInquiry.availableBalance
      )
      setGiftCardError('')
    } else if (
      extensionData?.giftCardExtensionBalanceInquiry?.__typename ===
      'GiftCardBalanceInquiryError'
    ) {
      setGiftCardError(extensionData.giftCardExtensionBalanceInquiry.message)
      removeGiftCard()
    }
    setLoadingGiftCard(false)
  }, [
    extensionData,
    setGiftCardNumber,
    setGiftCardAvailableBalance,

    setGiftCardError,
    removeGiftCard
  ])

  const applyGiftCard = async (
    cartGuid: string,
    giftCard: string,
    captchaToken?: string
  ) => {
    setLoadingGiftCard(true)
    await giftCardBalanceQuery({
      variables: {
        input: {
          cartGuid,
          cardNumber: giftCard.replace(/\s/g, ''),
          reCaptchaToken: captchaToken
        }
      }
    })
  }

  const applyGiftCardExtension = async (
    cartGuid: string,
    giftCard: string,
    captchaToken: string,
    pin?: string
  ) => {
    setLoadingGiftCard(true)

    if (pin !== undefined) {
      pin = pin.replace(/\s/g, '')
      setVerificationCode(pin)
    }

    giftCardBalanceExtensionQuery({
      variables: {
        input: {
          cartGuid,
          captchaToken,
          pin,
          cardNumber: giftCard.replace(/\s/g, '')
        }
      }
    })
  }

  return (
    <GiftCardContext.Provider
      value={{
        applyGiftCard,
        applyGiftCardExtension,
        removeGiftCard,
        loadingGiftCard,
        giftCardNumber,
        giftCardGuid,
        verificationCode,
        giftCardAvailableBalance,
        giftCardError
      }}
    >
      {props.children}
    </GiftCardContext.Provider>
  )
}

export const useGiftCard = () => {
  const context = useContext(GiftCardContext)
  if (!context) {
    throw new Error('useGiftCard must be used within a GiftCardContextProvider')
  }

  return context
}
